import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { AlertCard, getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';

interface TaskOption {
  taskId: string;
  title: string;
}

export type BulkTaskAction = 'approved' | 'not-applicable';

interface Props {
  open: boolean;
  onClose: () => void;
  onCancel: () => void;
  tasks: TaskOption[];
  profileIds: string[];
  action: BulkTaskAction;
  onSuccess: () => void;
}

const ACTION_LABELS: Record<BulkTaskAction, string> = {
  approved: 'Complete',
  'not-applicable': 'Mark as N/A',
};

const ALL_ITEMS = [
  { label: 'All tasks', value: 'all' },
  { label: 'None of tasks', value: 'none' },
];

export function BulkTaskActionDialog({ open, onClose, onCancel, tasks, profileIds, action, onSuccess }: Props) {
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = useApiUrl();
  const { run } = useCampfireFetch({ defer: true });
  const { setSnackbar } = useSnackbar();
  const { lightTextColor } = getAlertCardColors('info');

  const actionLabel = ACTION_LABELS[action];

  const onSelectTasks = (event: React.ChangeEvent<{ value: unknown }>) => {
    const values = event.target.value as string[];
    if (values.includes('all') && !values.includes('none')) {
      if (selectedTasks.includes('all')) {
        setSelectedTasks(tasks.map((t) => t.taskId));
      } else {
        setSelectedTasks(['all']);
      }
    } else if (values.includes('none')) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(values);
    }
  };

  const onSubmit = () => {
    const taskIds = selectedTasks.filter((id) => id !== 'all');
    if (taskIds.length === 0) return;

    setSubmitting(true);
    const completedTasks = profileIds.flatMap((profileId) =>
      taskIds.map((taskId) => ({
        profileId,
        taskId,
        status: action,
      }))
    );

    run({
      method: 'post',
      url: `${apiUrl}/vm/volunteer/profile/completed-task/import-bulk`,
      data: { completedTasks },
    })
      .then((response) => {
        const result = response.data?.data;
        const skipped: Array<{ error?: string }> = result?.skippedTasks ?? [];

        if (response.ok && skipped.length === 0) {
          setSnackbar({
            open: true,
            message: `${actionLabel}: ${taskIds.length} task(s) for ${profileIds.length} volunteer(s)`,
            variant: 'success',
          });
          setSelectedTasks([]);
          onSuccess();
          onClose();
        } else if (response.ok) {
          const reason = skipped[0]?.error ?? 'some tasks could not be saved';
          setSnackbar({
            open: true,
            message: `No tasks were saved — ${reason}`,
            variant: 'error',
          });
        } else {
          setSnackbar({ open: true, message: `Failed to ${actionLabel.toLowerCase()} task(s)`, variant: 'error' });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const taskOptions = ALL_ITEMS.concat(tasks.map((t) => ({ label: t.title, value: t.taskId })));

  const DialogActions = () => (
    <React.Fragment>
      <Button
        onClick={onSubmit}
        variant='contained'
        color='primary'
        style={{ textTransform: 'none' }}
        disabled={selectedTasks.length === 0 || submitting}
      >
        {submitting ? <CircularProgress size={20} /> : actionLabel}
      </Button>
    </React.Fragment>
  );

  return (
    <FullscreenDialog
      title={`${actionLabel} Tasks`}
      open={open}
      close={onCancel}
      fullScreen={false}
      dialogActions={DialogActions}
    >
      <Box>
        <Box paddingBottom={'10px'}>
          <AlertCard title={`${actionLabel} Tasks`} variant='info'>
            <Typography style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              Select one or many tasks below to {actionLabel.toLowerCase()} for the selected volunteers.
            </Typography>
          </AlertCard>
        </Box>
        <TextField
          style={{ marginTop: '1rem' }}
          fullWidth
          variant='outlined'
          select
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: () => {
              if (selectedTasks.length === 0) {
                return 'Select tasks';
              }
              if (selectedTasks.includes('all') || selectedTasks.length >= 3) {
                return `${selectedTasks.filter((id) => id !== 'all').length} tasks`;
              }
              return selectedTasks
                .map((id) => tasks.find((t) => t.taskId === id))
                .filter(Boolean)
                .map((t) => t?.title)
                .join(', ');
            },
          }}
          onChange={onSelectTasks}
          value={selectedTasks}
          label='Tasks'
        >
          {taskOptions.map((item) => {
            if (item.value === 'all') {
              return (
                <MenuItem key={item.value} value={item.value}>
                  <ListItemText primary='Select All' />
                </MenuItem>
              );
            }
            if (item.value === 'none') {
              return (
                <MenuItem key={item.value} value={item.value}>
                  <ListItemText primary='Deselect All' />
                </MenuItem>
              );
            }
            return (
              <MenuItem key={item.value} value={item.value}>
                <Checkbox
                  color='primary'
                  checked={selectedTasks.includes(item.value) || selectedTasks.includes('all')}
                />
                <ListItemText primary={item.label} />
              </MenuItem>
            );
          })}
        </TextField>
      </Box>
    </FullscreenDialog>
  );
}
